"use client";

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import type { DeliveryListItem } from "../../../services/merchantApi";
import { StatusPill, Tag, TimeText } from "../ui";
import { formatDuration, hostOf } from "./format";
import { DELIVERY_STATUS_UI, SKIP_REASON_LABEL, TRIGGER_TAG } from "./vocab";
import { HttpStatusChip } from "./HttpStatusChip";

// ────────────────────────────────────────────────────────────────────────────
// DeliveryRow — one line of the delivery history. The WHOLE row is a link to
// /developers/webhooks/deliveries/:id; it renders an <li>, so wrap rows in
// <ul className="divide-y divide-gray-800/50">.
//
//   sm+   [pill] checkout.session.completed [TEST]   host   [HTTP 500]  182 ms  2m ago  ›
//         attempt 3 of 6 · next try in 1h 52m
//   <sm   [pill] checkout.session.completed [TEST]
//         host · 2m ago                                                 [HTTP 500]
//         attempt 3 of 6 · next try in 1h 52m
//
// Nothing scrolls sideways (the app hides scrollbars globally): long values
// truncate. Every string that came from the merchant's server (URL, error
// message) is rendered as React text only.
// ────────────────────────────────────────────────────────────────────────────

const IN_PROGRESS = new Set(["pending", "delivering", "retrying"]);

function isDue(iso: string | null): boolean {
  if (!iso) return true;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) || ms <= Date.now();
}

// The second line: what is going on with this delivery right now. Null for
// a plain successful delivery (the first line already says everything).
function detailLine(d: DeliveryListItem): { tone: string; icon?: ReactNode; body: ReactNode } | null {
  const max = Math.max(1, d.maxAttempts || 1);

  if (d.delayed && IN_PROGRESS.has(d.status)) {
    return {
      tone: "text-amber-300",
      icon: <Clock className="w-3 h-3 shrink-0" aria-hidden="true" />,
      body: (
        <>
          Queued longer than expected
          {d.attemptCount > 0 && ` · attempt ${d.attemptCount} of ${max} failed`}
        </>
      ),
    };
  }

  switch (d.status) {
    case "retrying":
      return {
        tone: "text-gray-400",
        body: (
          <>
            attempt {Math.max(1, d.attemptCount)} of {max} ·{" "}
            {isDue(d.nextAttemptAt) ? (
              "retrying now"
            ) : (
              <>
                next try <TimeText iso={d.nextAttemptAt} interactive={false} />
              </>
            )}
          </>
        ),
      };
    case "pending":
      return {
        tone: "text-gray-400",
        body: isDue(d.nextAttemptAt) ? (
          "Queued · first attempt any moment"
        ) : (
          <>
            Queued · first attempt <TimeText iso={d.nextAttemptAt} interactive={false} />
          </>
        ),
      };
    case "delivering":
      return {
        tone: "text-blue-300",
        body: `Sending attempt ${Math.min(max, d.attemptCount + 1)} of ${max}…`,
      };
    case "skipped":
      return {
        tone: "text-gray-400",
        body: `skipped · ${d.skipReason ? SKIP_REASON_LABEL[d.skipReason] ?? d.skipReason : "not sent"}`,
      };
    case "failed": {
      const lead = max > 1 ? `All ${max} attempts failed` : "Attempt failed";
      return {
        tone: "text-red-300/80",
        body: d.lastErrorMessage ? `${lead} · ${d.lastErrorMessage}` : lead,
      };
    }
    default:
      return null;
  }
}

/**
 * One delivery in a history list.
 * @param delivery     the list item from GET /deliveries
 * @param showEndpoint show the endpoint host (default true); pass false when
 *                     the list is already filtered to a single endpoint
 */
export default function DeliveryRow({
  delivery: d,
  showEndpoint = true,
}: {
  delivery: DeliveryListItem;
  showEndpoint?: boolean;
}) {
  const ui = DELIVERY_STATUS_UI[d.status] ?? DELIVERY_STATUS_UI.pending;
  const tag = TRIGGER_TAG[d.trigger];
  const host = hostOf(d.endpointUrl);
  const duration = d.lastDurationMs != null ? formatDuration(d.lastDurationMs) : null;
  const detail = detailLine(d);

  return (
    <li>
      <Link
        to={`/developers/webhooks/deliveries/${encodeURIComponent(d.id)}`}
        className="group block px-4 sm:px-5 py-3.5 hover:bg-white/[0.02] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#61CE70]/60"
      >
        {/* Line 1 — status, event, tag; on sm+ also host, HTTP, duration, time. */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <StatusPill ui={ui} className="shrink-0" />
          <span className="min-w-0 truncate font-mono text-[12px] sm:text-[13px] text-gray-100">
            {d.eventType}
          </span>
          {tag && (
            <span className="shrink-0">
              <Tag tone={tag.tone}>{tag.label}</Tag>
            </span>
          )}

          <span className="hidden sm:flex items-center gap-3 ml-auto pl-2 shrink-0">
            {showEndpoint && (
              <span className="block max-w-[9rem] lg:max-w-[15rem] truncate text-xs text-gray-400">
                {host}
              </span>
            )}
            <span className="w-[5.5rem] flex justify-end">
              <HttpStatusChip status={d.lastResponseStatus} errorCode={d.lastErrorCode} />
            </span>
            <span className="w-14 text-right text-xs text-gray-500 tabular-nums">{duration}</span>
            <span className="w-16 text-right text-xs text-gray-500">
              <TimeText iso={d.createdAt} />
            </span>
            <ChevronRight
              className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors"
              aria-hidden="true"
            />
          </span>
        </div>

        {/* Line 2 on phones — host · time, HTTP chip on the right. (The
            duration yields its room to the host here; the detail page has it.) */}
        <div className="sm:hidden mt-1.5 flex items-center gap-1.5 min-w-0 text-xs text-gray-500">
          {showEndpoint && host && (
            <>
              <span className="min-w-0 truncate text-gray-400">{host}</span>
              <span aria-hidden="true">·</span>
            </>
          )}
          <span className="shrink-0">
            <TimeText iso={d.createdAt} />
          </span>
          {!showEndpoint && duration && (
            <>
              <span aria-hidden="true">·</span>
              <span className="shrink-0 tabular-nums">{duration}</span>
            </>
          )}
          <span className="ml-auto pl-1 shrink-0">
            <HttpStatusChip status={d.lastResponseStatus} errorCode={d.lastErrorCode} />
          </span>
        </div>

        {detail && (
          <p className={`mt-1 flex items-center gap-1.5 min-w-0 text-xs ${detail.tone}`}>
            {detail.icon}
            <span className="min-w-0 truncate">{detail.body}</span>
          </p>
        )}
      </Link>
    </li>
  );
}
